import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useCart } from "../../context/CartContext"; // Adjust path
import { useWishlist } from "../../context/WishlistContext"; // Adjust path
 
// Responsive ProductCard — Tailwind CSS
// - Image becomes taller on larger screens using responsive heights
// - Product title wraps naturally (no truncation) and scales with viewport
// - Layout adapts: stacked on small screens, horizontal-ish spacing on larger
// - Accessible buttons and aria-labels
 
const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
 
  const handleProduct = () => {
    navigate(`/products/${product.id}`);
  };
 
  const wishlisted = isInWishlist(product.id);
 
  const toggleWishlist = () => {
    if (wishlisted) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };
 
  const handleCartIcon = () => {
    // Explicitly set price to offer_price
    addToCart({ 
      ...product, 
      price: product.price, 
      offer_price: product.offer_price, 
      quantity: 1 
    });
    navigate("/cart");
  };
 
  return (
    <article className="w-full max-w-full bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Image area */}
      <div
        className="relative w-full cursor-pointer"
        onClick={handleProduct}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" ? handleProduct() : null)}
        aria-label={`View ${product.name}`}
      >
        {/* Responsive height: small screens shorter, larger screens taller */}
        <div className="w-full h-44 sm:h-56 md:h-64 lg:h-72 xl:h-80 flex items-center justify-center bg-white">
          <img
            src={product.image}
            alt={product.name}
            className="max-h-full object-contain w-full px-4"
            loading="lazy"
          />
        </div>
 
        {/* Wishlist button (absolute) */}
        <button
          className="absolute top-3 right-3 p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2"
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
          onClick={(e) => {
            e.stopPropagation();
            toggleWishlist();
          }}
        >
          <Heart
            size={22}
            strokeWidth={1.5}
            className={`transition-colors ${
              wishlisted ? "fill-red-600 text-red-600" : "text-neutral-500 hover:text-red-500"
            }`}
          />
        </button>
      </div>
 
      {/* Content area */}
      <div className="px-4 py-3 flex flex-col gap-3">
        {/* Title: allow wrapping and scaling with responsive font sizes. No truncation so full title is visible on all screens. */}
        <h3 className="text-neutral-800 font-semibold text-sm sm:text-base md:text-lg lg:text-xl leading-tight break-words whitespace-normal">
          {product.name}
        </h3>
 
        {/* Price + Add-to-cart row */}
        <div className="flex items-center justify-between gap-3">
          <div>
            {(() => {
              const originalPrice = Number(product.price) || 0;
              const offerPrice = Number(product.offer_price) || 0;

              const hasDiscount =
                offerPrice > 0 && offerPrice < originalPrice;

              const discountPercent = hasDiscount
                ? Math.round(((originalPrice - offerPrice) / originalPrice) * 100)
                : 0;

              return (
                <div className="flex items-center space-x-2">
                  {/* Offer Price */}
                  <span className="text-sm sm:text-base md:text-lg font-semibold text-neutral-700">
                    ₹{hasDiscount ? offerPrice : originalPrice}
                  </span>

                  {/* Original Price */}
                  {hasDiscount && (
                    <span className="text-sm sm:text-base md:text-lg font-medium text-neutral-400 line-through">
                      ₹{originalPrice}
                    </span>
                  )}

                  {/* Discount Percentage */}
                  {hasDiscount && (
                    <span className="text-xs sm:text-sm md:text-base font-semibold text-green-600">
                      {discountPercent}% OFF
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
 
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCartIcon();
              }}
              aria-label={`Add ${product.name} to cart`}
              className="inline-flex items-center justify-center px-3 py-2 rounded-full border border-neutral-200 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              {/* Cart icon (kept simple and responsive) */}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className="text-neutral-700"
              >
                <path
                  d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V16.5M9 19.5C9.8 19.5 10.5 20.2 10.5 21S9.8 22.5 9 22.5 7.5 21.8 7.5 21 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21S20.8 22.5 20 22.5 18.5 21.8 18.5 21 19.2 19.5 20 19.5Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
 
          </div>
        </div>
 
       
      </div>
    </article>
  );
};
 
export default ProductCard;